import {
  Box,
  Button,
  Flex,
  Menu,
  Tabs,
  Text,
  Tooltip,
  Skeleton,
} from "@mantine/core";
import { useContext, useEffect, useState } from "react";
import { ArrayValue } from '../../../general/ArrayValue';
import IdeaContext from "../../IdeaContext";
import BagelAutocomplete from "../../../general/BagelAutocomplete";
import { useAuth0 } from "@auth0/auth0-react";
import { saveRequest } from "../../../../Api";
import { showNotification } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons";
import { capitalizeFirstLetter } from "../../../../utils/capitalizeFirstLetter";
import React from "react";
import { formatNumberValue } from "../../Funcs";
import DiscoveryContext from "../../../Discovery/DiscoveryContext";
import { updateEvidence } from "../../../../api/evidences";
import BagelAccordionMenu from "../../../general/BagelAccordionMenu";
import MultipleAccountsInfo from "./MultipleAccountsInfo";
import EditableFieldRow from "./EditableFieldRow";
import { useEvidenceFieldUpdate } from "../../../../hooks/useEvidenceFieldUpdate";
import { useIdeaInfoTabsConfig } from "../../../../hooks/ideaPage/useIdeaQueries";
import {useKnowledgeData} from "../../../../hooks/useKnowledgeData";
import {useFields} from "../../../../hooks/useFields";
import { useUserPermissions } from "../../../../hooks/useUserPermissions";
import { resolveFieldWithFallback, evaluateDisplayIf, parseNumericBoundsFromPlaceholder } from "./infotab-utils";

const CardInfo = ({ 
  item,
  ideaPage,
  isInAccordian,
  activeTab, 
  setActiveTab,
  setDetailsOpened, 
  componentId,
  setComponentId,
  idea: propIdea,
  loadEvidence: propLoadEvidence
}
       :
  { 
    item: any;
    ideaPage: boolean,
    isInAccordian: boolean,
    activeTab: any,
    setActiveTab: any,
    setDetailsOpened: (isOpen: boolean) => void,
    componentId: string,
    setComponentId: React.Dispatch<React.SetStateAction<any>>,
    isCustomerPage: boolean,
    idea?: any,
    loadEvidence?: () => void
            }
) => {
  // console.log("CardInfo",{item})
  const auth0 = useAuth0();
  const {
    components,
    allComponents,
    opportunityAdditionalInfo,
    accountAdditionalInfo,
  } = useFields();

  const { owners, domains, evidenceDomain } = useKnowledgeData();
  const { canEditIdeas, isGtmManager, isGtm, isAgent } = useUserPermissions();
  const isReadOnly = isGtmManager || isGtm || isAgent;
  const { data: ideaInfoTabsConfigData, isLoading: isLoadingInfoTabs } = useIdeaInfoTabsConfig();

  const ContextToUse: React.Context<any> = ideaPage
    ? IdeaContext
    : DiscoveryContext;

  const contextData = useContext(ContextToUse);
  const { idea: contextIdea, loadEvidence: contextLoadEvidence } = contextData;
  
  // Use props if provided, otherwise use context
  const finalIdea = propIdea || contextIdea;
  const finalLoadEvidence = propLoadEvidence || contextLoadEvidence;

  const [domain, setDomain] = useState<any>(
    evidenceDomain ? item?.componentObj?.domain : null
  );
  const [productAreaName, setProductAreaName] = useState(
    item?.componentObj?.name
  );
  const [componentsByDomain, setComponentsByDomain] = useState<any[]>(
    evidenceDomain ? [] : components
  );
  const [owner, setOwner] = useState(item?.owner);
  const [ownerName, setOwnerName] = useState(item?.owner?.name || item?.ownerName);
  const [ownerId, setOwnerId] = useState(item?.owner?._id ?? item?.owner?.id);
  const domainsNames = domains.map((d: any) => d.name);
  const isMatch: boolean = item?.match;
  const company = item?.company;
  const [selectedComponent, setSelectedComponent] = useState(item?.componentObj);
 
  const existingAdditionalFieldsAccount = accountAdditionalInfo
    ? Object.keys(accountAdditionalInfo).filter((key) => company?.[key])
    : [];
  const existingAdditionalFieldsOpportunity = opportunityAdditionalInfo
    ? Object.keys(opportunityAdditionalInfo).filter((key) => company?.[key])
    : [];

  const onSelectSave = (type: string, value: string, ownerId = null) => {
    let updatedValue = value;

    if (type === "componentId") {
      const found = allComponents.find((a: any) => a?._id === value);
      updatedValue = found?.name;
    }

    if (item) {
      const itemObj = {
        id: item?._id,
        [type]: value,
      };
      if (ownerId) {
        itemObj["ownerId"] = ownerId;
      }

      updateEvidence(item?._id, {componentId: value}, auth0)
        .then(() => {
          showNotification({
            title: `${
              type === "componentId"
                ? "Product area"
                : type === "priority"
                  ? "Priority"
                  : "Domain"
            } updated`,
            color: "teal",
            message: `${
              type === "componentId"
                ? "Product area"
                : type === "priority"
                  ? "Priority"
                  : "Domain"
            } updated to "${updatedValue}" successfully`,
            icon: <IconCheck size="1.1rem" />,
            autoClose: 1200,
          });
          finalLoadEvidence();
        })
        .catch((e: any) => console.log(e));
    }
  };

  const saveProductArea = (productAreaName: string, componentId: string) => {
    setProductAreaName(productAreaName);
    setComponentId(componentId);
  };

  const copy = [...allComponents];
  const allComponentsIds = copy.map((c: any) => c?._id);
  const filterComponentByDomain = (domainValue: string, currentComponentId: any = componentId) => {
    const arr: any[] =
      components.filter((s: any) => s.domain === domainValue) || [];
    setComponentsByDomain(arr);
    const filtered =
      allComponents.filter((s: any) => s?.domain === domainValue) || [];
    const ids = filtered.map((a: any) => a?._id);
    if (!ids.includes(currentComponentId)) {
      setProductAreaName(null);
      setComponentId(null);
    }
  };

  const editOwner = (ownerObj: any, notification?: boolean) => {
    const itemObj = {
      id: item?._id,
      ownerId: ownerObj?.id,
    };

    saveRequest(itemObj, auth0).then(() => {
      if (notification) {
        showNotification({
          title: "Owner updated",
          color: "teal",
          message: `Request owner updated to "${ownerObj?.name}" successfully`,
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200,
        });
      }
      finalLoadEvidence();
    });
  };

  const handleSelect = (item: any) => {
    setProductAreaName(item.name);
    saveProductArea(item.name, item?._id);
    onSelectSave("componentId", item?._id, item?.ownerId);
  };

  useEffect(() => {
    setActiveTab("details");
  }, [item?._id]);

  useEffect(() => {
    setOwnerName(item?.owner?.name);
    setOwner(item?.owner);
    setOwnerId(item?.owner?._id ?? item?.owner?.id);

    setSelectedComponent(item?.componentObj);
    // Always set productAreaName to clear stale values when switching items
    const newProductAreaName = item?.componentObj?.name || null;
    setProductAreaName(newProductAreaName);

    // Check the new value, not the stale state value
    if (!newProductAreaName || newProductAreaName === "Unassigned") {
      if (item?.origin === "salesforce" && finalIdea?.componentId) {
        setProductAreaName(finalIdea?.componentObj?.name);
        evidenceDomain && filterComponentByDomain(finalIdea?.componentObj?.domain, finalIdea?.componentObj?._id ?? finalIdea?.componentId);
      }
    }
    if (evidenceDomain && allComponentsIds.includes(item?.componentObj?._id)) {
      filterComponentByDomain(item?.componentObj?.domain, item?.componentObj?._id);
    }
    if (
      evidenceDomain &&
      domain &&
      !allComponentsIds.includes(item?.componentObj?._id)
    ) {
      filterComponentByDomain(domain);
    } else {
      setComponentId(item?.componentObj?._id);
    }
  }, [item?._id, item?.owner, item?.componentObj]);

  // Generate tabs and content based on ideaInfoTabsConfig
  const tabsConfig = ideaInfoTabsConfigData?.ideaInfoTabsConfig || {};
  const tabKeys = Object.keys(tabsConfig);

  // Filter tabs based on displayIf condition (excluding "details" as it's hardcoded)
  const filteredTabs = tabKeys.filter((tabKey) => {
    if (tabKey === 'details') return false; // Skip details tab in the dynamic tabs loop
    const tab = tabsConfig[tabKey];
    if(item?.isContainer && item?.childEvidences?.length > 0) {
      // ✅ Only check children if present
      return item.childEvidences.some((child: any) =>
        evaluateDisplayIf(child, tab.displayIf)
      );
    }
    return evaluateDisplayIf(item, tab.displayIf);
  });

  // Check if there's a "details" tab configuration for additional fields
  const detailsTabConfig = tabsConfig['details'];
  const hasConfigurableDetailsFields = detailsTabConfig && detailsTabConfig.properties && detailsTabConfig.properties.length > 0;

  // Use React Query hook for field updates
  const { updateField, isFieldUpdating } = useEvidenceFieldUpdate({
    evidenceId: item?._id,
  });

  const domainComponentMap = domains.map((domain: any) => {
    
    const childComponents = components
      .filter((comp: any) => comp.domain?.toLowerCase()?.trim() === domain.name?.toLowerCase()?.trim())
      .map((comp: any) => ({
        ...comp,
        id: comp?._id,
        childComponents: comp.childComponents || []
      }));

    return {
      ...domain,
      id: domain?._id,
      name: domain.name,
      ownerName: domain.ownerName,
      productComponents: childComponents
    };
  });
  return (
    <Tabs value={activeTab} onTabChange={(value) => {
      if (isInAccordian) {
        setDetailsOpened(true);
        setTimeout(() => setActiveTab(value), 0); 
      } else {
        setActiveTab(value);
      }
    }}>
      {
        isInAccordian && 
        <Tabs.List>
          {/* Hardcoded Details Tab */}
          <Tabs.Tab value="details" sx={{padding: "16.6px"}}>
            <Text
              ta="left"
              fz={12}
              fw={600}
              color={activeTab === "details" ? "#212529" : "#5C5CEB"}
            >
              Details
            </Text>
          </Tabs.Tab>

          {/* Dynamically Generated Tabs */}
          {!isLoadingInfoTabs && filteredTabs.map((tabKey) => {
            const tab = tabsConfig[tabKey];
            if (!tab) return null;
            return (
              <Tabs.Tab key={tabKey} value={tabKey}>
                <Text
                  ta="left"
                  fz={12}
                  fw={600}
                  color={activeTab === tabKey ? "#212529" : "#5C5CEB"}
                >
                  {tab.label}
                </Text>
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
      }
      
      {
        !isInAccordian && 
        <>
          {/* Details Tab Panel */}
          <Tabs.Panel value="details" >
            <Box mt={12}>

              {/* Product Area */}
              <Flex h={32} align="center">
                <Text fz={12} h={16} w={120}>
                  Product area
                </Text>
                {isReadOnly ? (
                  <Text fz={12} color="dimmed">
                    {selectedComponent?.name || productAreaName || "Unassigned"}
                  </Text>
                ) : (
                  <BagelAccordionMenu
                    selectedComponent={selectedComponent}
                    isAiSuggestion={false}
                    onSelect={(item: any, type: string) => {
                      if (type === 'Domain') {
                        setDomain(item.name);
                      } else {
                        setProductAreaName(item?.name);
                        setSelectedComponent(item);
                        if (item.ownerId) {
                          setOwnerName(item?.ownerName);
                          setOwnerId(item.ownerId);
                        }
                        handleSelect(item);
                      }
                    }}
                  >
                    <Tooltip
                      maw={200}
                      multiline={true}
                      position="bottom"
                      disabled={!(ideaPage &&
                        isMatch &&
                        selectedComponent?.name &&
                        selectedComponent?.name !== finalIdea?.componentObj?.name)}
                      label={
                        ideaPage &&
                          isMatch &&
                          selectedComponent?.name &&
                          selectedComponent?.name !== finalIdea?.componentObj?.name
                          ? "This product area was set manually by a Bagel user. It does not match the Product Area of the Idea."
                          : ""
                      }
                    >
                      <Menu.Target>
                        <Flex
                          h="32px"
                          fz={12}
                          sx={{
                            cursor: "pointer",
                            opacity: !domain && evidenceDomain ? 0.5 : 1,
                          }}
                          align="center"
                        >
                          <Button variant="subtle" compact color="gray">
                            <Text fz={12} color={
                              ideaPage &&
                                isMatch &&
                                selectedComponent?.name &&
                                selectedComponent?.name !== finalIdea?.componentObj?.name
                                ? "red"
                                : "black"
                            }>
                              {selectedComponent?.name && selectedComponent?.name !== "Unassigned" && <Text span color={
                                ideaPage &&
                                  isMatch &&
                                  selectedComponent?.name &&
                                  selectedComponent?.name !== finalIdea?.componentObj?.name
                                  ? "red"
                                  : "dimmed"
                              }
                              >.../ </Text>} {selectedComponent?.name || productAreaName || "Unassigned"}</Text>
                          </Button>
                        </Flex>
                      </Menu.Target>
                    </Tooltip>
                  </BagelAccordionMenu>
                )}
              </Flex>

              {/* Owner */}
              <Flex h={32} align="center">
                <Text fz={12} h={16} w={120}>
                  Owner
                </Text>
                {isReadOnly ? (
                  <Text fz={12} color="dimmed">
                    {capitalizeFirstLetter(ownerName) || "Unassigned"}
                  </Text>
                ) : (
                  <BagelAutocomplete
                    items={owners.map((member: any) => ({
                      name: member.name,
                      id: member?._id,
                      useId: true,
                    }))}
                    selected={ownerId}
                    onSelect={(ownerItem: any) => {
                      setOwnerName(ownerItem?.name);
                      setOwnerId(ownerItem?.id);
                      setOwner(ownerItem);
                      editOwner(ownerItem, true);
                    }}
                  >
                    <Tooltip label="Select owner">
                      <Button variant="subtle" compact color="gray">
                        <Text fz={12} color="black">{capitalizeFirstLetter(ownerName) || "Unassigned"}</Text>
                      </Button>
                    </Tooltip>
                  </BagelAutocomplete>
                )}
              </Flex>

              {/* Configurable details tab fields */}
              {isLoadingInfoTabs && (
                <>
                  <Skeleton h={32} w="100%" />
                  <Skeleton h={32} w="100%" />
                  <Skeleton h={32} w="100%" />
                </>
              )}
              {!isLoadingInfoTabs && hasConfigurableDetailsFields && detailsTabConfig?.properties?.map((property: any) => {
                let value = resolveFieldWithFallback(item, property.fieldPath);
                const rawValue = value; // Keep raw value for editable fields

                // Format value for display (non-editable fields only)
                if (value === undefined || value === null || value === "") {
                  value = "N/A";
                } else if (typeof value === "number") {
                  // Only format numbers for non-editable fields or when not type number
                  value = property.editable && property.type === 'number' ? value : formatNumberValue(value, property.prefix);
                } else if (typeof value === "boolean") {
                  value = value ? "Yes" : "No";
                } else if (typeof value === "object") {
                  return null;
                }

                const fieldPathForSave = property.dbFieldPath || property.fieldPath;
                // Config-driven min/max win when present; otherwise fall back to a
                // "(min-max)" pattern in the placeholder (e.g. "Enter score (1-10)").
                const numericBounds = property.type === 'number'
                  ? parseNumericBoundsFromPlaceholder(property.placeholder)
                  : {};

                return (
                  <EditableFieldRow
                    key={property.fieldPath}
                    label={property.label}
                    value={property.type === 'number' && property.editable ? rawValue : value}
                    editable={property.editable && canEditIdeas}
                    type={property.type || 'text'}
                    options={property.options || []}
                    placeholder={property.placeholder}
                    required={property.required}
                    min={property.min ?? numericBounds.min}
                    max={property.max ?? numericBounds.max}
                    fieldPath={fieldPathForSave}
                    evidenceId={item?._id}
                    isUpdating={isFieldUpdating(fieldPathForSave)}
                    onSave={async (newValue) => {
                      updateField({
                        fieldPath: fieldPathForSave,
                        value: newValue,
                        fieldType: property.type,
                      });
                    }}
                  />
                );
              })}
            </Box>
          </Tabs.Panel>

          {/* Dynamically Generated Tabs Panels */}
          {!isLoadingInfoTabs && filteredTabs.map((tabKey) => {
            const tab = tabsConfig[tabKey];
            if (!tab) return null;
            return (
              item.isContainer && item.hasChildren ? 
                <Tabs.Panel key={tabKey} value={tabKey} id="meir" >
                  <MultipleAccountsInfo item={item} tab={tab} />
                </Tabs.Panel>
                :
                <Tabs.Panel key={tabKey} value={tabKey} id="meir">
                  <Box mt={12}>
                    {tab.properties?.map((property: any) => {
                      let value = resolveFieldWithFallback(item, property.fieldPath);
                      const rawValue = value;
                      const fieldPathForSave = property.dbFieldPath || property.fieldPath;

                      // Array: render with comma separation + "show more"
                      if (Array.isArray(value)) {
                        if (value.length === 0) return null;
                        return (
                          <Flex key={property.fieldPath} align="flex-start" sx={{ minHeight: 22 }}>
                            <Text fz={12} w={120} sx={{ flexShrink: 0 }}>
                              {property.label}
                            </Text>
                            <ArrayValue items={value} />
                          </Flex>
                        );
                      }

                      if (value === undefined || value === null || value === "") {
                        value = "N/A";
                      } else if (typeof value === "number") {
                        // Only skip formatting when the editable number input will be shown;
                        // otherwise formatted value is used by the plain Text display.
                        const willShowEditableNumber = !isReadOnly && property.editable && canEditIdeas && property.type === 'number';
                        value = willShowEditableNumber
                          ? value
                          : formatNumberValue(value, property.prefix);
                      } else if (typeof value === "boolean") {
                        value = value ? "Yes" : "No";
                      }

                      const showEditable = !isReadOnly && property.editable && canEditIdeas;
                      // Config-driven min/max win when present; otherwise fall back to a
                      // "(min-max)" pattern in the placeholder (e.g. "Enter score (1-10)").
                      const numericBounds = property.type === 'number'
                        ? parseNumericBoundsFromPlaceholder(property.placeholder)
                        : {};

                      return showEditable ? (
                        <EditableFieldRow
                          key={property.fieldPath}
                          label={property.label}
                          value={property.type === 'number' ? rawValue : value}
                          editable={true}
                          type={property.type || 'text'}
                          options={property.options || []}
                          placeholder={property.placeholder}
                          required={property.required}
                          min={property.min ?? numericBounds.min}
                          max={property.max ?? numericBounds.max}
                          fieldPath={fieldPathForSave}
                          evidenceId={item?._id}
                          isUpdating={isFieldUpdating(fieldPathForSave)}
                          onSave={async (newValue) => {
                            updateField({
                              fieldPath: fieldPathForSave,
                              value: newValue,
                              fieldType: property.type,
                            });
                          }}
                        />
                      ) : (
                        <Flex key={property.fieldPath} h={32} align="center">
                          <Text fz={12} h={16} w={120}>
                            {property.label}
                          </Text>
                          <Text fz={12}>{value}</Text>
                        </Flex>
                      );
                    })}
                  </Box>
                </Tabs.Panel>
            );
          })}
        </>
      }
    </Tabs>
  );
};

export default CardInfo;
